import { NavLink, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useSession } from "../lib/session";
import { api, type Role } from "../lib/api";
import { useTheme, type ThemePreference } from "../lib/theme";

const THEMES: { value: ThemePreference; label: string }[] = [
  { value: "light", label: "Light" },
  { value: "auto", label: "Auto" },
  { value: "dark", label: "Dark" },
];

/**
 * Sits on the blue header, so it is styled against that rather than with the
 * form SegmentedControl -- same shape, header colours. Radio semantics, since
 * exactly one of the three is always in effect.
 */
function ThemeToggle() {
  const { preference, setPreference } = useTheme();
  return (
    <div
      role="radiogroup"
      aria-label="Colour theme"
      className="flex overflow-hidden rounded border border-white/40"
    >
      {THEMES.map((option) => {
        const selected = preference === option.value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => setPreference(option.value)}
            className={`px-2 py-1 text-xs font-medium transition ${
              selected ? "bg-white text-uoa-blue" : "text-white/80 hover:bg-white/10 hover:text-white"
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

const NAV: { to: string; label: string; roles: Role[] }[] = [
  { to: "/", label: "Dashboard", roles: ["STAFF", "COORDINATOR"] },
  // Positions leads for students and industry because that is where they now
  // land; My EOI sits second rather than first so the nav matches it.
  { to: "/positions", label: "Positions", roles: ["STUDENT", "STAFF", "COORDINATOR", "INDUSTRY"] },
  { to: "/profile", label: "My EOI", roles: ["STUDENT"] },
  { to: "/references", label: "References", roles: ["STUDENT", "STAFF", "COORDINATOR"] },
  { to: "/students", label: "Students", roles: ["INDUSTRY"] },
  { to: "/admin", label: "Admin", roles: ["COORDINATOR"] },
];

export function Layout({ children }: { children: React.ReactNode }) {
  const { user, signOut } = useSession();
  const { data: config } = useQuery({
    queryKey: ["config"],
    queryFn: () => api.get<{ demoMode: boolean }>("/config"),
    staleTime: Infinity,
  });

  return (
    <div className="flex min-h-screen flex-col">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded focus:bg-surface focus:px-4 focus:py-2">
        Skip to content
      </a>

      <header className="bg-uoa-blue text-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3">
          <Link to="/" className="flex items-center gap-3">
            {/* TODO: replace with the supplied University of Auckland logo asset. */}
            <span aria-hidden className="grid h-9 w-9 place-items-center rounded bg-white font-bold text-uoa-blue">
              IS
            </span>
            <span className="text-lg font-semibold tracking-tight">Intern-Seek</span>
          </Link>

          {/* Outside the `user` check: the theme is a preference, not an
              account setting, so it stays available however Layout is used. */}
          <div className="flex items-center gap-3 text-sm">
            <ThemeToggle />
            {user && (
              <>
                <span className="hidden sm:inline text-white/80">
                  {user.name ?? user.email} &middot; {user.role.toLowerCase()}
                </span>
                {config?.demoMode && (
                  <Link
                    to="/demo"
                    className="rounded bg-amber-400 px-3 py-1 font-medium text-amber-950 hover:bg-amber-300"
                  >
                    Switch persona
                  </Link>
                )}
                <button onClick={signOut} className="rounded border border-white/40 px-3 py-1 hover:bg-white/10">
                  Sign out
                </button>
              </>
            )}
          </div>
        </div>

        {user && (
          <nav aria-label="Main" className="border-t border-white/15">
            <ul className="mx-auto flex max-w-6xl gap-1 overflow-x-auto px-4">
              {NAV.filter((item) => item.roles.includes(user.role)).map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.to === "/"}
                    className={({ isActive }) =>
                      `inline-block whitespace-nowrap border-b-2 px-3 py-2 text-sm ${
                        isActive ? "border-white font-semibold" : "border-transparent text-white/80 hover:text-white"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>
        )}
      </header>

      <main id="main" className="mx-auto w-full max-w-6xl flex-1 px-4 py-8">
        {children}
      </main>

      <footer className="border-t border-line bg-surface">
        <div className="mx-auto max-w-6xl px-4 py-6 text-sm text-uoa-grey">
          Intern-Seek &middot; University of Auckland &middot;{" "}
          <Link className="underline hover:text-uoa-blue dark:hover:text-uoa-blue-light" to="/privacy">Privacy</Link>
          {config?.demoMode && (
            <>
              {" "}&middot;{" "}
              <Link className="underline hover:text-uoa-blue dark:hover:text-uoa-blue-light" to="/welcome">Landing page</Link>
            </>
          )}
        </div>
      </footer>
    </div>
  );
}
